import { API_BASE_URL } from '../../../../../config';
import { FORM_CODE_FF011 } from './healthInfoZuidoTypes';
import type { HealthInfoZuidoFormValues } from './healthInfoZuidoTypes';

export const DONE_STEP_ID = '__done__';

export type HealthInfoZuidoProgressPayload = {
  values: HealthInfoZuidoFormValues;
  currentStepId: string | null;
};

type ApiProgress = {
  introContinued?: string;
  consentDocumentRead?: string;
  agreeForm31?: string;
  agreeForm32?: string;
  forms312PreviewDone?: string;
  workHistoryIntroContinued?: string;
  workHistoryFormViewed?: string;
  workHistoryInfoCorrect?: string;
  workHistoryCorrectionCategory?: string;
  currentStepId?: string | null;
};

function mapValuesFromApi(data: ApiProgress): HealthInfoZuidoFormValues {
  const values: HealthInfoZuidoFormValues = {};
  if (data.introContinued === 'true' || data.introContinued === 'false') {
    values.introContinued = data.introContinued;
  }
  if (data.consentDocumentRead === 'true' || data.consentDocumentRead === 'false') {
    values.consentDocumentRead = data.consentDocumentRead;
  }
  if (data.agreeForm31 === 'yes' || data.agreeForm31 === 'no') {
    values.agreeForm31 = data.agreeForm31;
  }
  if (data.agreeForm32 === 'yes' || data.agreeForm32 === 'no') {
    values.agreeForm32 = data.agreeForm32;
  }
  if (data.forms312PreviewDone === 'true' || data.forms312PreviewDone === 'false') {
    values.forms312PreviewDone = data.forms312PreviewDone;
  }
  if (data.workHistoryIntroContinued === 'true' || data.workHistoryIntroContinued === 'false') {
    values.workHistoryIntroContinued = data.workHistoryIntroContinued;
  }
  if (data.workHistoryFormViewed === 'true' || data.workHistoryFormViewed === 'false') {
    values.workHistoryFormViewed = data.workHistoryFormViewed;
  }
  if (data.workHistoryInfoCorrect === 'yes' || data.workHistoryInfoCorrect === 'no') {
    values.workHistoryInfoCorrect = data.workHistoryInfoCorrect;
  }
  if (data.workHistoryCorrectionCategory) {
    values.workHistoryCorrectionCategory = data.workHistoryCorrectionCategory;
  }
  return values;
}

function appendValuesToBody(
  body: Record<string, string | null>,
  values: HealthInfoZuidoFormValues,
  clearFields: ReadonlyArray<keyof HealthInfoZuidoFormValues> = []
): void {
  if (values.introContinued === 'true' || values.introContinued === 'false') {
    body.introContinued = values.introContinued;
  }
  if (values.consentDocumentRead === 'true' || values.consentDocumentRead === 'false') {
    body.consentDocumentRead = values.consentDocumentRead;
  }
  if (values.agreeForm31 === 'yes' || values.agreeForm31 === 'no') {
    body.agreeForm31 = values.agreeForm31;
  }
  if (values.agreeForm32 === 'yes' || values.agreeForm32 === 'no') {
    body.agreeForm32 = values.agreeForm32;
  }
  if (values.forms312PreviewDone === 'true' || values.forms312PreviewDone === 'false') {
    body.forms312PreviewDone = values.forms312PreviewDone;
  }
  if (values.workHistoryIntroContinued === 'true' || values.workHistoryIntroContinued === 'false') {
    body.workHistoryIntroContinued = values.workHistoryIntroContinued;
  }
  if (values.workHistoryFormViewed === 'true' || values.workHistoryFormViewed === 'false') {
    body.workHistoryFormViewed = values.workHistoryFormViewed;
  }
  if (values.workHistoryInfoCorrect === 'yes' || values.workHistoryInfoCorrect === 'no') {
    body.workHistoryInfoCorrect = values.workHistoryInfoCorrect;
  }
  if (values.workHistoryCorrectionCategory) {
    body.workHistoryCorrectionCategory = values.workHistoryCorrectionCategory;
  }
  // Empty string tells the backend to null out a previously saved field (undo).
  for (const field of clearFields) {
    if (values[field] == null) {
      body[field] = '';
    }
  }
}

export function clearedHealthInfoZuidoFields(
  before: HealthInfoZuidoFormValues,
  after: HealthInfoZuidoFormValues
): Array<keyof HealthInfoZuidoFormValues> {
  const keys: Array<keyof HealthInfoZuidoFormValues> = [
    'introContinued',
    'consentDocumentRead',
    'agreeForm31',
    'agreeForm32',
    'forms312PreviewDone',
    'workHistoryIntroContinued',
    'workHistoryFormViewed',
    'workHistoryInfoCorrect',
    'workHistoryCorrectionCategory',
  ];
  return keys.filter((key) => before[key] != null && after[key] == null);
}

export async function fetchHealthInfoZuidoProgress(
  email: string,
  invitationId: string
): Promise<HealthInfoZuidoProgressPayload | null> {
  if (!API_BASE_URL || !email?.trim() || !invitationId?.trim()) return null;
  const params = new URLSearchParams({
    email: email.trim(),
    invitationId: invitationId.trim(),
    formCode: FORM_CODE_FF011,
  });
  const r = await fetch(`${API_BASE_URL}/api/health-info-zuido/progress?${params.toString()}`);
  if (!r.ok) return null;
  const data = (await r.json()) as ApiProgress;
  return {
    values: mapValuesFromApi(data),
    currentStepId: data.currentStepId ?? null,
  };
}

export async function upsertHealthInfoZuidoProgress(params: {
  email: string;
  invitationId: string;
  values: HealthInfoZuidoFormValues;
  currentStepId: string | null;
  clearFields?: ReadonlyArray<keyof HealthInfoZuidoFormValues>;
}): Promise<HealthInfoZuidoProgressPayload | null> {
  if (!API_BASE_URL || !params.email?.trim() || !params.invitationId?.trim()) return null;
  try {
    const body: Record<string, string | null> = {
      email: params.email.trim(),
      invitationId: params.invitationId.trim(),
      formCode: FORM_CODE_FF011,
      currentStepId: params.currentStepId,
    };
    appendValuesToBody(body, params.values, params.clearFields);
    const r = await fetch(`${API_BASE_URL}/api/health-info-zuido/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      console.warn('Health info zuido save failed', r.status);
      return null;
    }
    const data = (await r.json()) as ApiProgress;
    return {
      values: mapValuesFromApi(data),
      currentStepId: data.currentStepId ?? params.currentStepId,
    };
  } catch (e) {
    console.warn('Health info zuido save failed', e);
    return null;
  }
}

export async function markForms312PreviewDone(
  email: string,
  invitationId: string
): Promise<HealthInfoZuidoProgressPayload | null> {
  return upsertHealthInfoZuidoProgress({
    email,
    invitationId,
    values: { forms312PreviewDone: 'true' },
    currentStepId: 'workHistoryIntro',
  });
}
